import React, { useMemo, useCallback, useState, type ReactNode } from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import * as DocumentPicker from "expo-document-picker";
import { Check, Info, Trash2 } from "lucide-react-native";
import {
  DocumentRow,
  IconButton,
  MoonCheckbox,
  DateField,
} from "@louise/moon-ui";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
import { DocumentFileTypeIcon } from "@src/packages/document-vault/DocumentUploadProcessingStep";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import { QuestionLayout } from "@src/packages/medical-history/components";
import {
  useDocuments,
  useDeleteDocument,
} from "@src/packages/documents/hooks/useDocuments";
import { useDocumentSourceTransfer } from "@src/packages/documents/ingestion";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import { useTranslations } from "@src/packages/i18n";
import { useFamilyHistory } from "../FamilyHistoryContext";
import type { FamilyHistoryStackParamList } from "../FamilyHistoryNavigator";
import { useMedicalHistoryTheme } from "../../theme/MedicalHistoryTheme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type DnaReportFile = {
  id: string;
  name: string;
};

type DnaReportFileStatus = "uploading" | "filing" | "saved" | "error";

function DnaReportFileRow({
  file,
  status,
  error,
  onDelete,
  onRetry,
}: {
  file: DnaReportFile;
  status: DnaReportFileStatus;
  error?: string | null;
  onDelete?: () => void;
  onRetry?: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("DnaTestReportRow");
  const isActive = status === "uploading" || status === "filing";
  const statusLabel =
    status === "uploading"
      ? t("documents.upload.source_uploading")
      : status === "filing"
        ? t("documents.upload.source_filing")
        : status === "error"
          ? error || t("documents.upload.failure.status.not_saved")
          : t("documents.upload.source_saved");

  let trailing: ReactNode;
  if (isActive) {
    trailing = (
      <ThinkingOrbStatus
        state={status === "uploading" ? "composing" : "shaping"}
        label={statusLabel}
        size={20}
      />
    );
  } else if (status === "error") {
    trailing = (
      <View style={styles.rowActions}>
        {onRetry ? (
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("documents.upload.retry_source")),
                "documents.upload.retry_source",
              );
              onRetry();
            }}
            accessibilityRole="button"
          >
            <Text style={styles.retryLabel}>
              {t("documents.upload.retry_source")}
            </Text>
          </Pressable>
        ) : null}
        {onDelete ? (
          <IconButton
            accessibilityLabel={t("med_history.medical.shared.remove")}
            action="remove_dna_report"
            onPress={onDelete}
            size="sm"
            pressedOpacity={0.2}
          >
            <Trash2 size={16} color="#8A7F78" />
          </IconButton>
        ) : null}
      </View>
    );
  } else {
    trailing = (
      <View style={styles.rowActions}>
        <View style={styles.savedMarker}>
          <Check size={14} color="#6B625C" strokeWidth={2.5} />
        </View>
        {onDelete ? (
          <IconButton
            accessibilityLabel={t("med_history.medical.shared.remove")}
            action="remove_dna_report"
            onPress={onDelete}
            size="sm"
            pressedOpacity={0.2}
          >
            <Trash2 size={16} color="#8A7F78" />
          </IconButton>
        ) : null}
      </View>
    );
  }

  return (
    <DocumentRow
      accessibilityLabel={`${file.name}, ${statusLabel}`}
      title={file.name}
      subtitle={statusLabel}
      leading={<DocumentFileTypeIcon fileName={file.name} />}
      trailing={trailing}
      showChevron={false}
      titleNumberOfLines={1}
      subtitleNumberOfLines={2}
      style={styles.fileRow}
    />
  );
}

export function DnaTestReportScreen() {
  const t = useTranslations();
  const { state, updateState } = useFamilyHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<FamilyHistoryStackParamList>>();
  const theme = useMedicalHistoryTheme();

  const deleteDocument = useDeleteDocument();
  const transfer = useDocumentSourceTransfer();
  const [pendingFile, setPendingFile] = useState<SelectedFile | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const { data: allDocuments = [] } = useDocuments();

  // Find previously uploaded DNA ancestry reports from the documents API
  const existingDnaReports = useMemo(() => {
    return allDocuments
      .filter((doc) => doc.tags?.some((tag) => tag === "dna-ancestry-report"))
      .map((doc) => ({
        id: doc.id,
        name:
          doc.name || t("med_history.family_history.dna_report_default_name"),
        uri: doc.uploads?.[0]?.url ?? "",
      }));
  }, [allDocuments, t]);

  // Merge: show API-persisted docs + any just-uploaded doc from this session
  const uploadedFiles = useMemo<DnaReportFile[]>(() => {
    const apiIds = new Set(existingDnaReports.map((d) => d.id));
    // If the session-local ID already matches an API doc, don't duplicate
    const sessionFile =
      state.dnaAncestryDocumentId && !apiIds.has(state.dnaAncestryDocumentId)
        ? [
            {
              id: state.dnaAncestryDocumentId,
              name: t("med_history.family_history.dna_report_default_name"),
              uri: "",
            },
          ]
        : [];
    return [...existingDnaReports, ...sessionFile];
  }, [existingDnaReports, state.dnaAncestryDocumentId, t]);
  const hasAnyReport = pendingFile !== null || uploadedFiles.length > 0;

  const handleUpload = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const file = result.assets[0]!;
      const sourceFile = {
        uri: file.uri,
        name: file.name,
        mimeType: resolveUploadContentType({
          name: file.name,
          uri: file.uri,
          mimeType: file.mimeType,
        }),
        size: file.size ?? undefined,
      };
      setPendingFile(sourceFile);
      setUploadError(null);
      const { documentId: docId } = await transfer.transfer(sourceFile, {
        name: file.name,
        tags: ["dna-ancestry-report", "medical_records"],
        destination: "lab_result",
      });
      updateState({ dnaAncestryDocumentId: docId });
      setPendingFile(null);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Upload failed");
    }
  }, [transfer, updateState]);

  const retryUpload = useCallback(async () => {
    if (!pendingFile) return;
    setUploadError(null);
    try {
      const documentId = transfer.uploadId
        ? await transfer.retryFile()
        : (
            await transfer.transfer(pendingFile, {
              name: pendingFile.name,
              tags: ["dna-ancestry-report", "medical_records"],
              destination: "lab_result",
            })
          ).documentId;
      updateState({ dnaAncestryDocumentId: documentId });
      setPendingFile(null);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Upload failed");
    }
  }, [pendingFile, transfer, updateState]);

  const handleToggleDontHave = (dontHaveReport: boolean) => {
    updateState({
      dontHaveReport,
      ...(dontHaveReport
        ? {
            dnaAncestryDocumentId: null,
            dnaAncestryReportDate: null,
          }
        : {}),
    });
  };

  const handleNext = () => {
    navigation.navigate("Adopted");
  };

  return (
    <QuestionLayout
      title={t("med_history.family_history.dna_report_title")}
      onBack={() => navigation.goBack()}
      onSave={handleNext}
      saveDisabled={false}
      saveLabel={t("med_history.shared.next")}
    >
      <View style={styles.container}>
        <Text style={[styles.sectionLabel, { color: theme.text }]}>
          {t("med_history.family_history.dna_report_section_label")}
        </Text>

        {!state.dontHaveReport && (
          <>
            <View style={styles.infoRow}>
              <Info size={14} color={theme.textFaint} />
              <Text style={[styles.infoText, { color: theme.textFaint }]}>
                {t("med_history.family_history.dna_report_info")}
              </Text>
            </View>

            <View style={styles.filesField}>
              {pendingFile ? (
                <DnaReportFileRow
                  file={{ id: pendingFile.uri, name: pendingFile.name }}
                  status={
                    uploadError
                      ? "error"
                      : transfer.state === "filing"
                        ? "filing"
                        : "uploading"
                  }
                  error={uploadError}
                  onRetry={() => void retryUpload()}
                  onDelete={() => {
                    transfer.reset();
                    setPendingFile(null);
                    setUploadError(null);
                  }}
                />
              ) : null}
              {uploadedFiles.map((file) => (
                <DnaReportFileRow
                  key={file.id}
                  file={file}
                  status="saved"
                  onDelete={() => {
                    deleteDocument.mutate(file.id);
                    updateState({ dnaAncestryDocumentId: null });
                  }}
                />
              ))}
              {!pendingFile ? (
                <DocumentFileDropZone
                  onPick={() => void handleUpload()}
                  compact
                  primaryLabel={t(
                    hasAnyReport
                      ? "med_history.family_history.dna_report_add_another"
                      : "med_history.components.file_upload.upload_cta",
                  )}
                  secondaryLabel={t(
                    "med_history.components.file_upload.file_types",
                  )}
                />
              ) : null}
            </View>

            <View style={styles.dateContainer}>
              <DateField
                label={t(
                  "med_history.family_history.dna_report_test_date_label",
                )}
                value={state.dnaAncestryReportDate ?? ""}
                onChange={(iso) => {
                  updateState({ dnaAncestryReportDate: iso || null });
                }}
              />
            </View>
          </>
        )}

        {!hasAnyReport ? (
          <MoonCheckbox
            value={state.dontHaveReport}
            onChange={handleToggleDontHave}
            label={t("med_history.family_history.dna_report_dont_have")}
            style={styles.checkbox}
          />
        ) : null}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  filesField: {
    gap: 8,
  },
  fileRow: {
    paddingVertical: 8,
  },
  rowActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  savedMarker: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(42, 37, 34, 0.06)",
  },
  retryLabel: {
    color: "#7A3E15",
    fontSize: 11,
    fontWeight: "700",
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  infoText: {
    fontSize: 12,
    flex: 1,
  },
  dateContainer: {
    marginTop: 16,
  },
  checkbox: {
    marginTop: 24,
  },
});
